/**
 * TerminalPanel — терминал-стиль панель в Developer Mode.
 *
 * Mac-window chrome (traffic lights), copy-кнопка для каждой команды,
 * search по выводу, ANSI-подсветка (ansi_up).
 * Логика сбора данных не меняется — те же run_terminal actions из messages.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Terminal as TerminalIcon,
  ChevronUp,
  ChevronDown,
  Maximize2,
  Minimize2,
  Copy,
  Check,
  Search,
  X,
} from "lucide-react";
import { AnsiUp } from "ansi_up";
import { useChatsStore } from "../../store/chats";

interface TerminalEntry {
  id: string;
  command: string;
  output: string;
  ts: number;
}

export function TerminalPanel() {
  const session = useChatsStore((s) => s.sessions.find((x) => x.id === s.activeId));
  const messages = session?.messages || [];
  const [collapsed, setCollapsed] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const entries = useMemo<TerminalEntry[]>(() => {
    const out: TerminalEntry[] = [];
    messages.forEach((m) => {
      if (m.role !== "assistant" || !m.actions) return;
      m.actions.forEach((a, i) => {
        const isTerminal =
          a.name === "run_terminal" ||
          a.name === "shell" ||
          a.name === "execute" ||
          a.name?.includes("terminal");
        if (!isTerminal) return;
        const cmd = String((a.args as { command?: string } | undefined)?.command ?? "");
        if (!cmd && !a.result) return;
        out.push({
          id: `${m.id}-${i}`,
          command: cmd,
          output: a.result || "",
          ts: 0,
        });
      });
    });
    return out;
  }, [messages]);

  const ansi = useMemo(() => {
    const u = new AnsiUp();
    u.use_classes = false;
    return u;
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        e.command.toLowerCase().includes(q) || e.output.toLowerCase().includes(q),
    );
  }, [entries, search]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || collapsed) return;
    el.scrollTop = el.scrollHeight;
  }, [entries.length, collapsed]);

  function copyEntry(e: TerminalEntry) {
    const text = e.command ? `$ ${e.command}\n${e.output}` : e.output;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(e.id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  }

  if (entries.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`overflow-hidden rounded-modal border border-[#1f2937] shadow-card ${
        maximized ? "fixed inset-3 sm:inset-6 z-30" : "mt-3"
      }`}
      style={{ background: "#0a0e14" }}
    >
      {/* ─── Mac-window chrome ──────────────────────────────────────── */}
      <div
        className="flex items-center gap-2 px-3 py-2 border-b border-[#1f2937]"
        style={{ background: "#0d1117" }}
      >
        {/* Traffic lights */}
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="size-3 rounded-full bg-[#ff5f57]" aria-hidden />
          <span className="size-3 rounded-full bg-[#ffbd2e]" aria-hidden />
          <span className="size-3 rounded-full bg-[#28ca42]" aria-hidden />
        </div>
        {/* Title */}
        <div className="flex-1 flex items-center justify-center gap-2 text-[12px] font-mono text-[#9ca3af]">
          <TerminalIcon className="size-3.5 text-[#22d3ee]" />
          <span>terminal · {filtered.length}/{entries.length}</span>
        </div>
        {/* Actions */}
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => {
              setSearchOpen((v) => !v);
              if (searchOpen) setSearch("");
            }}
            className="size-7 grid place-items-center rounded text-[#9ca3af] hover:text-white hover:bg-[#1f2937]"
            aria-label="Поиск в логах"
            title="Поиск"
          >
            <Search className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setMaximized((v) => !v)}
            className="size-7 grid place-items-center rounded text-[#9ca3af] hover:text-white hover:bg-[#1f2937]"
            aria-label={maximized ? "Свернуть" : "Развернуть"}
            title={maximized ? "Свернуть" : "На весь экран"}
          >
            {maximized ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
          </button>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="size-7 grid place-items-center rounded text-[#9ca3af] hover:text-white hover:bg-[#1f2937]"
            aria-label={collapsed ? "Развернуть" : "Свернуть"}
          >
            {collapsed ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
          </button>
        </div>
      </div>

      {/* ─── Search bar ─────────────────────────────────────────────── */}
      <AnimatePresence>
        {searchOpen && !collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden border-b border-[#1f2937]"
          >
            <div className="px-3 py-1.5 flex items-center gap-2">
              <Search className="size-3 text-[#9ca3af] shrink-0" />
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск по команде или выводу…"
                className="flex-1 bg-transparent outline-none text-small font-mono text-white placeholder:text-[#6b7280]"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="text-[#9ca3af] hover:text-white"
                  aria-label="Очистить"
                >
                  <X className="size-3" />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Body ────────────────────────────────────────────────────── */}
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div
              ref={scrollRef}
              className={`overflow-auto font-mono text-[0.78rem] p-3 ${
                maximized ? "h-[calc(100vh-9rem)]" : "max-h-72"
              }`}
              style={{ background: "#0a0e14", color: "#a3e635" }}
            >
              {filtered.length === 0 ? (
                <div className="text-[#6b7280] italic">Ничего не найдено</div>
              ) : (
                filtered.map((e) => (
                  <TerminalEntryView
                    key={e.id}
                    entry={e}
                    ansi={ansi}
                    copied={copiedId === e.id}
                    onCopy={() => copyEntry(e)}
                    highlight={search.trim()}
                  />
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function TerminalEntryView({
  entry,
  ansi,
  copied,
  onCopy,
  highlight,
}: {
  entry: TerminalEntry;
  ansi: AnsiUp;
  copied: boolean;
  onCopy: () => void;
  highlight: string;
}) {
  const html = useMemo(() => {
    if (!entry.output) return "";
    let h = ansi.ansi_to_html(entry.output);
    if (highlight && highlight.length >= 2) {
      const safe = highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      try {
        h = h.replace(
          new RegExp(`(${safe})`, "gi"),
          '<mark style="background:#fbbf24;color:#000">$1</mark>',
        );
      } catch {
        /* invalid regex — игнорируем */
      }
    }
    return h;
  }, [entry.output, ansi, highlight]);

  return (
    <div className="group/entry relative mb-3">
      {entry.command && (
        <div className="text-[#22d3ee] mb-1 flex items-start gap-2 pr-8">
          <span className="text-[#6b7280] select-none">$</span>
          <span className="break-all">{entry.command}</span>
        </div>
      )}
      {html ? (
        <div
          className="whitespace-pre-wrap break-words"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <div className="text-[#6b7280] italic">(нет вывода)</div>
      )}
      {/* copy */}
      <button
        type="button"
        onClick={onCopy}
        className="absolute right-1 top-0 opacity-0 group-hover/entry:opacity-100 transition-opacity p-1 rounded text-[#9ca3af] hover:text-white hover:bg-[#1f2937]"
        aria-label="Скопировать"
        title="Скопировать команду и вывод"
      >
        {copied ? <Check className="size-3 text-[#a3e635]" /> : <Copy className="size-3" />}
      </button>
    </div>
  );
}
